/**
 * `add(calendar, tick, n, unit)` (1.K-1 deliverable 7): NOW's deltas and the solver's lags.
 * Minutes, hours, days and weeks (W days) add exact seconds. Months count regular months only, and
 * years keep the regular month and day; both clamp the day to the target month's length in the
 * target year and keep the time of day. A date on an intercalary day first moves to the last day
 * of the regular month before it in its year, or, when none precedes it, to the first day of the
 * one after it. Adding 0 of any unit gives the tick back unchanged.
 */
import { isTick, TIME_UNITS } from '@codex/contracts';
import { floorDiv, floorMod, mul, add as plus } from './math.js';
import { isCompiled } from './types.js';
import { locate, monthLength, monthOffset, yearIsLeap, yearStartTick } from './year.js';
export function add(calendar, tick, n, unit) {
    if (!isCompiled(calendar))
        return { ok: false, reason: 'calendar_invalid' };
    if (!Number.isSafeInteger(n) || !TIME_UNITS.includes(unit)) {
        return { ok: false, reason: 'incomplete_parts' };
    }
    const at = isTick(tick) ? locate(calendar, tick) : null;
    if (at === null)
        return { ok: false, reason: 'out_of_tick_range' };
    // Adding nothing changes nothing, even on an intercalary day (a decision of 1.K-1's report).
    if (n === 0)
        return { ok: true, tick };
    let result;
    if (unit === 'month' || unit === 'year') {
        let month = at.month;
        let day = at.day;
        if (month.intercalary) {
            const before = calendar.regularMonths.filter((m) => m.index < month.index);
            const previous = before[before.length - 1];
            month = previous ?? calendar.regularMonths[0];
            day = previous === undefined ? 1 : monthLength(previous, at.leap);
        }
        const r = calendar.regularMonths.length;
        const at2 = unit === 'month' ? plus(month.regularIndex, n) : month.regularIndex;
        const year = plus(at.year, unit === 'month' ? floorDiv(at2, r) : n);
        if (!Number.isSafeInteger(year))
            return { ok: false, reason: 'out_of_tick_range' };
        const target = calendar.regularMonths[floorMod(at2, r)];
        const leap = yearIsLeap(calendar, year);
        const clamped = Math.min(day, monthLength(target, leap));
        const days = monthOffset(target, leap) + clamped - 1;
        result = plus(plus(yearStartTick(calendar, year), mul(days, calendar.secondsPerDay)), at.second);
    }
    else {
        const seconds = unit === 'minute'
            ? calendar.secondsPerMinute
            : unit === 'hour'
                ? calendar.secondsPerHour
                : unit === 'day'
                    ? calendar.secondsPerDay
                    : mul(calendar.weekdays.length, calendar.secondsPerDay);
        result = plus(tick, mul(n, seconds));
    }
    return isTick(result) ? { ok: true, tick: result } : { ok: false, reason: 'out_of_tick_range' };
}
//# sourceMappingURL=add.js.map